import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, Headphones, ChevronRight, ChevronDown } from "lucide-react";

import { useWidgetTracking } from "@/hooks/useWidgetTracking";
import { useI18n } from "@/contexts/I18nContext";
import { analytics } from "@/lib/analytics/events";

interface SmartHelpWidgetProps {
  onOpenAIChat: () => void;
  onOpenWhatsApp: () => void;
}

const WHATSAPP_NUMBER = "41772317288";
const WHATSAPP_WELCOME = "¡Hola Punto Hispano! 👋 Le escribo desde su web y me gustaría recibir asesoramiento.";

const SmartHelpWidget = ({ 
  onOpenAIChat, 
  onOpenWhatsApp 
}: SmartHelpWidgetProps) => {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('helpWidgetDismissed') === 'true';
    }
    return false;
  });

  const { 
    createLead, 
    trackOptionSelected, 
    trackWhatsAppClick, 
    trackWidgetMinimized,
    trackWidgetExpanded 
  } = useWidgetTracking();
  
  const leadCreated = useRef(false);

  // Entry animation delay
  useEffect(() => {
    const entryTimer = setTimeout(() => {
      setHasEntered(true);
    }, 2000);
    return () => clearTimeout(entryTimer);
  }, []);

  // Auto-expand after 30 seconds if not dismissed
  useEffect(() => {
    if (!isDismissed && hasEntered) {
      const expandTimer = setTimeout(() => {
        setIsExpanded(true);
        trackWidgetExpanded();
      }, 30000);
      return () => clearTimeout(expandTimer);
    }
  }, [isDismissed, hasEntered, trackWidgetExpanded]);

  const handleMinimize = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(false);
    setIsDismissed(true);
    localStorage.setItem('helpWidgetDismissed', 'true');
    trackWidgetMinimized();
  }, [trackWidgetMinimized]);

  const menuOpenCountRef = useRef(0);
  const optionHoveredRef = useRef<Set<string>>(new Set());
  const scrollTrackedRef = useRef(false);

  const pushGAEvent = useCallback((action: string, label: string) => {
    analytics.widgetInteract(label, action);
  }, []);

  const handleClick = useCallback(async () => {
    if (!leadCreated.current) {
      await createLead();
      leadCreated.current = true;
    }
    menuOpenCountRef.current += 1;
    optionHoveredRef.current = new Set();
    scrollTrackedRef.current = false;
    pushGAEvent('menu_open', `help_widget_menu_open_${menuOpenCountRef.current}`);
    setIsMenuOpen(true);
    document.body.classList.add('help-menu-open');
  }, [createLead, pushGAEvent]);

  const handleOptionHover = useCallback((option: string) => {
    if (optionHoveredRef.current.has(option)) return;
    optionHoveredRef.current.add(option);
    pushGAEvent('option_hover', `help_widget_option_${option}`);
  }, [pushGAEvent]);

  const handleOptionsScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (scrollTrackedRef.current) return;
    const el = e.currentTarget;
    if (el.scrollTop > 8) {
      scrollTrackedRef.current = true;
      pushGAEvent('options_scroll', 'help_widget_options_scrolled');
    }
  }, [pushGAEvent]);

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
    document.body.classList.remove('help-menu-open');
  }, []);

  useEffect(() => {
    return () => {
      document.body.classList.remove('help-menu-open');
    };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        handleCloseMenu();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen, handleCloseMenu]);

  const handleWhatsApp = useCallback(async () => {
    pushGAEvent('whatsapp_click', 'help_widget_whatsapp');
    await trackWhatsAppClick();
    await trackOptionSelected("whatsapp");
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    const message = `${WHATSAPP_WELCOME}${currentUrl ? `\n\nPágina: ${currentUrl}` : ''}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
    handleCloseMenu();
  }, [pushGAEvent, trackWhatsAppClick, trackOptionSelected, handleCloseMenu]);


  const handleAIChat = useCallback(async () => {
    pushGAEvent('ai_chat_click', 'help_widget_ai');
    await trackOptionSelected("chat-ia");
    onOpenAIChat();
    handleCloseMenu();
  }, [pushGAEvent, onOpenAIChat, trackOptionSelected, handleCloseMenu]);

  if (!hasEntered) return null;

  return (
    <>
      <style>{`
        /* ============================================
           HELP WIDGET - COMPLETE STYLES
           ============================================ */

        @keyframes slideInUpBounce {
          0% {
            opacity: 0;
            transform: translateY(100px);
          }
          60% {
            opacity: 1;
            transform: translateY(-10px);
          }
          80% {
            transform: translateY(5px);
          }
          100% {
            transform: translateY(0);
          }
        }

        @keyframes subtleBounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }

        @keyframes pulseStatus {
          0%, 100% {
            opacity: 1;
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55);
          }
          50% {
            opacity: 0.85;
            box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
          }
        }

        @keyframes slideInOption {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes helpLabelFadeSlide {
          0% { opacity: 0; transform: translateY(-4px); }
          15%, 85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0.6; transform: translateY(0); }
        }
        @keyframes helpLabelShimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .help-widget-label {
          animation:
            helpLabelFadeSlide 4.5s ease-in-out 1.2s both,
            helpLabelShimmer 6s ease-in-out 5.8s infinite;
          background: linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(34,197,94,0.55) 50%, rgba(0,0,0,0.78) 100%);
          background-size: 200% 100%;
        }
        .help-widget-button:hover .help-widget-label {
          opacity: 1 !important;
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .help-widget-label {
            animation: none;
            opacity: 0.85;
            background: rgba(0,0,0,0.7);
          }
        }

        .help-widget-enter {
          animation: slideInUpBounce 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }


        .help-widget-icon-bounce {
          animation: subtleBounce 2s ease-in-out infinite;
          animation-delay: 5s;
        }

        /* Widget Button Styles */
        .help-widget-button {
          position: fixed;
          z-index: 9999;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          user-select: none;
          -webkit-user-select: none;
        }

        .help-widget-button:focus-visible {
          outline: 3px solid rgba(185, 28, 28, 0.5);
          outline-offset: 2px;
        }

        /* Mobile */
        @media (max-width: 767px) {
          .help-widget-button {
            bottom: calc(20px + var(--ph-sticky-offset, 0px));
            right: 16px;
          }
          .help-widget-circular {
            width: 56px;
            height: 56px;
          }
          .help-widget-icon {
            width: 28px;
            height: 28px;
          }
          .help-widget-expanded {
            padding: 12px 16px;
            font-size: 14px;
            max-width: 160px;
          }
        }

        /* Tablet */
        @media (min-width: 768px) and (max-width: 1023px) {
          .help-widget-button {
            bottom: calc(24px + var(--ph-sticky-offset, 0px));
            right: 24px;
          }
          .help-widget-circular {
            width: 58px;
            height: 58px;
          }
          .help-widget-icon {
            width: 30px;
            height: 30px;
          }
          .help-widget-expanded {
            padding: 13px 18px;
            font-size: 14px;
          }
        }

        /* Desktop */
        @media (min-width: 1024px) {
          .help-widget-button {
            bottom: 32px;
            right: 32px;
          }
          .help-widget-circular {
            width: 60px;
            height: 60px;
          }
          .help-widget-icon {
            width: 32px;
            height: 32px;
          }
          .help-widget-expanded {
            padding: 14px 20px;
            font-size: 15px;
          }
        }

        .help-widget-circular {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #25D366;
          box-shadow: 0 4px 12px rgba(37, 211, 102, 0.45);
        }

        .help-widget-circular:hover {
          transform: scale(1.08);
          background: #1ebe5d;
          box-shadow: 0 6px 16px rgba(37, 211, 102, 0.6);
        }

        .help-widget-circular:active {
          transform: scale(0.95);
        }

        .help-widget-expanded {
          border-radius: 28px;
          display: flex;
          align-items: center;
          gap: 10px;
          background: hsl(var(--primary));
          color: white;
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(185, 28, 28, 0.4);
        }

        .help-widget-expanded:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(185, 28, 28, 0.5);
        }

        .help-widget-expanded:active {
          transform: scale(0.98);
        }

        .help-widget-minimize-btn {
          position: absolute;
          top: -6px;
          right: -6px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.6);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0.8;
          transition: all 0.2s ease;
          border: none;
          padding: 0;
        }

        .help-widget-minimize-btn:hover {
          opacity: 1;
          transform: scale(1.1);
          background: rgba(0, 0, 0, 0.8);
        }

        .help-widget-online-dot {
          width: 8px;
          height: 8px;
          background: #22c55e;
          border-radius: 50%;
          animation: pulseStatus 2.4s ease-in-out infinite;
          flex-shrink: 0;
          will-change: opacity, box-shadow;
        }

        @media (prefers-reduced-motion: reduce) {
          .help-widget-online-dot {
            animation: none;
          }
        }

        /* ============================================
           MODAL STYLES - DARK THEME
           ============================================ */

        .help-modal {
          position: fixed;
          z-index: 10000;
        }

        /* Modal positioning - responsive */
        @media (max-width: 767px) {
          .help-modal {
            bottom: 90px;
            right: 16px;
          }
          .help-modal-content {
            width: calc(100vw - 32px);
            max-width: 360px;
          }
          .help-modal-header {
            padding: 16px;
          }
          .help-modal-mascot {
            width: 44px;
            height: 44px;
          }
          .help-modal-title {
            font-size: 14px;
          }
          .help-modal-options {
            padding: 12px;
            gap: 10px;
          }
          .help-modal-option {
            padding: 14px;
            gap: 12px;
          }
          .help-modal-option-icon {
            width: 44px;
            height: 44px;
          }
          .help-modal-option-title {
            font-size: 13px;
          }
          .help-modal-option-desc {
            font-size: 12px;
          }
        }

        @media (min-width: 768px) and (max-width: 1023px) {
          .help-modal {
            bottom: 96px;
            right: 24px;
          }
          .help-modal-content {
            width: 360px;
          }
        }

        @media (min-width: 1024px) {
          .help-modal {
            bottom: 100px;
            right: 32px;
          }
          .help-modal-content {
            width: 380px;
          }
        }

        .help-modal-content {
          background: #1a1a1a;
          border-radius: 16px;
          box-shadow: 
            0 20px 25px -5px rgba(0, 0, 0, 0.3),
            0 10px 10px -5px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }

        /* Header */
        .help-modal-header {
          padding: 20px;
          background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .help-modal-header-left {
          display: flex;
          gap: 12px;
          align-items: center;
          flex: 1;
        }

        .help-modal-mascot {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(185, 28, 28, 0.3);
        }

        .help-modal-header-text {
          flex: 1;
        }

        .help-modal-title {
          font-size: 15px;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 6px 0;
          letter-spacing: 0.5px;
        }

        .help-modal-status {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .help-modal-status-text {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.7);
        }

        .help-modal-close {
          width: 32px;
          height: 32px;
          border: none;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
          user-select: none;
        }

        .help-modal-close:hover {
          background: rgba(255, 255, 255, 0.15);
          color: #ffffff;
          transform: rotate(90deg);
        }

        .help-modal-close:focus-visible {
          outline: 2px solid #B91C1C;
          outline-offset: 2px;
        }

        /* Options */
        .help-modal-options {
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .help-modal-option {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: left;
          position: relative;
          overflow: hidden;
          user-select: none;
        }

        /* Gradient overlay on hover */
        .help-modal-option::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, rgba(185, 28, 28, 0.1) 0%, transparent 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        /* Shimmer effect */
        .help-modal-option::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(
            45deg,
            transparent 30%,
            rgba(255, 255, 255, 0.08) 50%,
            transparent 70%
          );
          transform: translateX(-100%);
          pointer-events: none;
        }

        .help-modal-option:hover::before {
          opacity: 1;
        }

        .help-modal-option:hover::after {
          animation: shimmer 0.6s ease forwards;
        }

        .help-modal-option:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(185, 28, 28, 0.5);
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
        }

        .help-modal-option:active {
          transform: translateY(0);
        }

        .help-modal-option:focus-visible {
          outline: 2px solid #B91C1C;
          outline-offset: 2px;
        }

        /* Featured option (AI Chat) */
        .help-modal-option-featured {
          border-color: #B91C1C;
          background: rgba(185, 28, 28, 0.1);
        }

        .help-modal-option-featured::before {
          opacity: 0.3;
        }

        .help-modal-option-featured:hover {
          border-color: #B91C1C;
          background: rgba(185, 28, 28, 0.15);
          box-shadow: 0 8px 20px rgba(185, 28, 28, 0.4);
        }

        .help-modal-option-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          transition: transform 0.3s ease;
          position: relative;
          z-index: 1;
        }

        .help-modal-option:hover .help-modal-option-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .help-modal-option-icon-live {
          background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
        }

        .help-modal-option-icon-ai {
          background: linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%);
        }

        .help-modal-option-icon-whatsapp {
          background: linear-gradient(135deg, #22C55E 0%, #16A34A 100%);
        }

        .help-modal-option-content {
          flex: 1;
          min-width: 0;
          position: relative;
          z-index: 1;
        }

        .help-modal-option-title {
          font-size: 14px;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 4px 0;
          letter-spacing: 0.5px;
        }

        .help-modal-option-desc {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
          margin: 0;
          line-height: 1.3;
        }

        .help-modal-option-arrow {
          color: rgba(255, 255, 255, 0.4);
          transition: all 0.3s ease;
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        }

        .help-modal-option:hover .help-modal-option-arrow {
          color: #B91C1C;
          transform: translateX(4px);
        }

        /* Staggered animation */
        .help-modal-option-animate-1 {
          animation: slideInOption 0.3s ease-out 0.1s both;
        }

        .help-modal-option-animate-2 {
          animation: slideInOption 0.3s ease-out 0.2s both;
        }

        .help-modal-option-animate-3 {
          animation: slideInOption 0.3s ease-out 0.3s both;
        }
      `}</style>

      {/* Main Widget Button */}
      <AnimatePresence mode="wait">
        {!isMenuOpen && (
          <motion.button
            key={isExpanded ? "expanded" : "circular"}
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            onClick={handleClick}
            className={`help-widget-button help-widget-enter group ${isExpanded ? 'help-widget-expanded' : 'help-widget-circular'}`}
            aria-label="Asistencia: contacta por WhatsApp o chat con IA"
            title="Asistencia: contacta por WhatsApp o chat con IA"
            role="button"
            tabIndex={0}
          >
            {!isExpanded && (
              <span
                className="pointer-events-none absolute right-[calc(100%+12px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-lg bg-[#1a1a1a] px-3 py-2 text-xs font-medium text-white shadow-lg opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-200"
                role="tooltip"
              >
                ¿Necesitas ayuda? Chat IA o WhatsApp
                <span className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-[#1a1a1a]" />
              </span>
            )}
            <div className={`relative flex items-center justify-center flex-shrink-0 ${isExpanded ? 'w-8 h-8' : 'help-widget-icon help-widget-icon-bounce'}`}>
              <svg
                viewBox="0 0 32 32"
                fill="currentColor"
                aria-hidden="true"
                className="w-[70%] h-[70%] text-white"
              >
                <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.792 1.23 1.82 2.612 3.41 4.55 4.453.515.273 2.526 1.117 3.064 1.117.872 0 2.706-.616 2.706-1.59 0-.143-.043-.27-.073-.4-.214-.428-1.404-1.317-1.748-1.376zm-2.964 6.32c-1.86 0-3.65-.532-5.198-1.55l-3.625.928 1.001-3.475c-1.118-1.605-1.748-3.51-1.748-5.474 0-5.27 4.296-9.566 9.567-9.566s9.566 4.297 9.566 9.566c0 5.27-4.296 9.566-9.566 9.566zm0-21c-6.293 0-11.434 5.14-11.434 11.434 0 1.992.515 3.953 1.49 5.668L3.668 28.05a.97.97 0 0 0 .945 1.252.93.93 0 0 0 .315-.043l5.79-1.633a11.413 11.413 0 0 0 5.328 1.305c6.293 0 11.434-5.14 11.434-11.434S22.44 2.526 16.146 2.526z"/>
              </svg>
              <span className="help-widget-online-dot absolute -top-0.5 -right-0.5 ring-2 ring-white" style={{ width: 10, height: 10 }} />
            </div>

            {isExpanded && (
              <>
                <span className="whitespace-nowrap">{t.helpWidget.help}</span>
                <span className="help-widget-online-dot" />
                <button
                  onClick={handleMinimize}
                  className="help-widget-minimize-btn"
                  aria-label={t.helpWidget.minimize}
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
              </>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Help Modal */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseMenu}
              className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-sm"
              aria-hidden="true"
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              className="help-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="help-modal-title"
            >
              <div className="help-modal-content">
                {/* Header */}
                <div className="help-modal-header">
                  <div className="help-modal-header-left">
                    <div className="help-modal-mascot">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="help-modal-header-text">
                      <h2 id="help-modal-title" className="help-modal-title">
                        {t.helpWidget.howCanWeHelp}
                      </h2>
                      <div className="help-modal-status">
                        <span className="help-widget-online-dot" />
                        <span className="help-modal-status-text">{t.helpWidget.online}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseMenu}
                    className="help-modal-close"
                    aria-label={t.helpWidget.closeMenu}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Options */}
                <div className="help-modal-options" onScroll={handleOptionsScroll}>

                  {/* AI Chat - Featured */}
                  <button 
                    onClick={handleAIChat}
                    onMouseEnter={() => handleOptionHover('ai-chat')}
                    onFocus={() => handleOptionHover('ai-chat')}
                    className="help-modal-option help-modal-option-featured help-modal-option-animate-2"
                  >
                    <div className="help-modal-option-icon help-modal-option-icon-ai">
                      <Headphones className="w-6 h-6 text-white" />
                    </div>
                    <div className="help-modal-option-content">
                      <h3 className="help-modal-option-title">{t.helpWidget.aiChat}</h3>
                      <p className="help-modal-option-desc">{t.helpWidget.aiChatDesc}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 help-modal-option-arrow" />
                  </button>

                  {/* WhatsApp */}
                  <button 
                    onClick={handleWhatsApp}
                    onMouseEnter={() => handleOptionHover('whatsapp')}
                    onFocus={() => handleOptionHover('whatsapp')}
                    className="help-modal-option help-modal-option-animate-3"
                  >
                    <div className="help-modal-option-icon help-modal-option-icon-whatsapp">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="help-modal-option-content">
                      <h3 className="help-modal-option-title">WHATSAPP</h3>
                      <p className="help-modal-option-desc">+41 77 231 72 88</p>
                    </div>
                    <ChevronRight className="w-5 h-5 help-modal-option-arrow" />
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default SmartHelpWidget;
